// 手繪筆：包裝 roughjs 產生器，輸出精簡、可重現的 SVG 片段。
import roughModule from 'roughjs';
import type { RoughGenerator } from 'roughjs/bin/generator.js';
import type { Drawable, Options as RoughOptions } from 'roughjs/bin/core.js';
import { getStroke } from 'perfect-freehand';
import { hash32 } from './model.js';
import { type Point, cubicAt } from './layout.js';

// roughjs 是 CommonJS 套件：Node 直接拿到產生器物件，型別宣告卻是 ESM default，這裡兩者皆相容。
const rough = ((roughModule as unknown as { default?: unknown }).default ?? roughModule) as { generator(): RoughGenerator };

export interface StrokeStyle {
  stroke: string;
  strokeWidth: number;
  fill?: string;
  fillStyle?: 'hachure' | 'solid' | 'cross-hatch' | 'zigzag';
  roughness?: number;
  dash?: string;
  opacity?: number;
  fillOpacity?: number;
}

const generator = rough.generator();

// 數字取到小數兩位並去掉尾數 0，讓輸出精簡且穩定。
export function fmt(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

// 把路徑字串中的浮點數全部縮短。
function compactPath(d: string): string {
  return d.replace(/-?\d*\.\d+(?:e-?\d+)?/g, match => fmt(Number(match))).replace(/\s+/g, ' ').trim();
}

// 由識別字串產生 roughjs 種子（正整數）。
export function seedOf(key: string): number {
  return (hash32(key) % 2147483646) + 1;
}

// 把 roughjs Drawable 轉成 <path> 元素。
function emit(drawable: Drawable, style: StrokeStyle): string {
  return generator.toPaths(drawable).map(path => {
    const isFill = path.stroke === 'none' || (path.fill && path.fill !== 'none');
    const attrs = [`d="${compactPath(path.d)}"`];
    if (path.stroke && path.stroke !== 'none') {
      attrs.push(`stroke="${path.stroke}"`, `stroke-width="${fmt(path.strokeWidth)}"`, 'fill="none"', 'stroke-linecap="round"', 'stroke-linejoin="round"');
      if (style.dash && !isFill && path.stroke === style.stroke) attrs.push(`stroke-dasharray="${style.dash}"`);
      if (style.fillOpacity !== undefined && path.stroke !== style.stroke) attrs.push(`stroke-opacity="${style.fillOpacity}"`);
    } else {
      attrs.push(`fill="${path.fill}"`, 'stroke="none"');
      if (style.fillOpacity !== undefined) attrs.push(`fill-opacity="${style.fillOpacity}"`);
    }
    return `<path ${attrs.join(' ')}/>`;
  }).join('');
}

// 轉成 roughjs 選項；比照 Excalidraw「藝術家」筆觸。
function roughOptions(style: StrokeStyle, seed: string): RoughOptions {
  return {
    seed: seedOf(seed),
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    roughness: style.roughness ?? 1,
    bowing: 1,
    fill: style.fill,
    fillStyle: style.fillStyle ?? 'hachure',
    fillWeight: style.strokeWidth / 2,
    hachureGap: style.strokeWidth * 4,
    hachureAngle: -41,
    preserveVertices: true,
  };
}

// 圓角矩形路徑（Excalidraw 的 round 角樣式）。
export function roundedRectPath(x: number, y: number, w: number, h: number, radius: number): string {
  const r = Math.min(radius, w / 2, h / 2);
  return `M${fmt(x + r)} ${fmt(y)}L${fmt(x + w - r)} ${fmt(y)}Q${fmt(x + w)} ${fmt(y)} ${fmt(x + w)} ${fmt(y + r)}`
    + `L${fmt(x + w)} ${fmt(y + h - r)}Q${fmt(x + w)} ${fmt(y + h)} ${fmt(x + w - r)} ${fmt(y + h)}`
    + `L${fmt(x + r)} ${fmt(y + h)}Q${fmt(x)} ${fmt(y + h)} ${fmt(x)} ${fmt(y + h - r)}`
    + `L${fmt(x)} ${fmt(y + r)}Q${fmt(x)} ${fmt(y)} ${fmt(x + r)} ${fmt(y)}Z`;
}

export const pen = {
  roundedRect(x: number, y: number, w: number, h: number, radius: number, style: StrokeStyle, seed: string): string {
    return emit(generator.path(roundedRectPath(x, y, w, h, radius), roughOptions(style, seed)), style);
  },
  ellipse(cx: number, cy: number, w: number, h: number, style: StrokeStyle, seed: string): string {
    return emit(generator.ellipse(cx, cy, w, h, roughOptions(style, seed)), style);
  },
  line(x1: number, y1: number, x2: number, y2: number, style: StrokeStyle, seed: string): string {
    return emit(generator.line(x1, y1, x2, y2, roughOptions(style, seed)), style);
  },
  polyline(points: Point[], style: StrokeStyle, seed: string): string {
    return emit(generator.linearPath(points.map(point => [point.x, point.y] as [number, number]), roughOptions(style, seed)), style);
  },
  polygon(points: Point[], style: StrokeStyle, seed: string): string {
    return emit(generator.polygon(points.map(point => [point.x, point.y] as [number, number]), roughOptions(style, seed)), style);
  },
  path(d: string, style: StrokeStyle, seed: string): string {
    return emit(generator.path(d, roughOptions(style, seed)), style);
  },
};

// 以種子產生 [-1, 1) 的可重現亂數序列。
export function seededRandom(key: string): () => number {
  let state = hash32(key) || 1;
  return () => {
    state ^= state << 13; state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5; state >>>= 0;
    return (state / 0x100000000) * 2 - 1;
  };
}

// 錐形主幹：沿貝茲曲線取樣，壓力由粗漸細，用 perfect-freehand 產生外框後填色。
export function taperedStroke(points: Point[], width: number, color: string, seed: string): string {
  const random = seededRandom(seed);
  // 控制點輕微抖動，避免每條主幹一模一樣。
  const jittered = points.map((point, index) => index === 0 || index === points.length - 1 ? point : { x: point.x + random() * 6, y: point.y + random() * 6 });
  const length = Math.hypot(jittered[3]!.x - jittered[0]!.x, jittered[3]!.y - jittered[0]!.y);
  const samples = Math.max(12, Math.round(length / 5));
  const input: [number, number, number][] = [];
  for (let index = 0; index <= samples; index++) {
    const t = index / samples;
    const point = cubicAt(jittered, t);
    input.push([point.x, point.y, 1 - t * 0.72]);
  }
  const outline = getStroke(input, {
    size: width, thinning: 0.7, smoothing: 0.6, streamline: 0.2, simulatePressure: false, last: true,
    start: { cap: true, taper: false }, end: { cap: true, taper: false },
  });
  return `<path d="${outlineToPath(outline)}" fill="${color}" stroke="none"/>`;
}

// perfect-freehand 外框點 → 平滑封閉路徑（中點二次曲線）。
function outlineToPath(outline: number[][]): string {
  if (outline.length < 4) return '';
  const parts = [`M${fmt(outline[0]![0]!)} ${fmt(outline[0]![1]!)}Q`];
  for (let index = 0; index < outline.length; index++) {
    const [x0, y0] = outline[index]!;
    const [x1, y1] = outline[(index + 1) % outline.length]!;
    parts.push(`${fmt(x0!)} ${fmt(y0!)} ${fmt((x0! + x1!) / 2)} ${fmt((y0! + y1!) / 2)} `);
  }
  return parts.join('').trim() + 'Z';
}
